/**
 * Shared status → pill-variant mapping used by every list page.
 *
 * The four pill variants come from `features/backlog/backlog.css`:
 *   • ok    — green, success/done states          (Approved, Delivered, Sent, …)
 *   • warn  — amber, attention/in-progress        (Pending Approval, Waiting, …)
 *   • info  — blue,  neutral / informational      (Pending Validation, Pending Delivery, …)
 *   • alert — red,   failure / blocked            (Failed, Cancelled, …)
 *
 * Before this helper existed, Subout, Analysis, and Reporting each had their
 * own mapping — same status word could end up green on one form and amber on
 * another.  Centralising the rule here means "Approved" looks identical in
 * the Result Approval grid and the Report Tracking grid.
 *
 * Add new statuses here when a new SP starts emitting them; never patch a
 * per-page mapping again.
 */
export type StatusVariant = 'ok' | 'warn' | 'info' | 'alert';

/**
 * Returns the pill class variant for a status label.  Case- and
 * whitespace-insensitive; matches the longest applicable rule.
 */
export function statusVariant(label: string | null | undefined): StatusVariant {
  if (!label) return 'info';
  const s = label.trim().toLowerCase();

  // ── Success / done — green ───────────────────────────────────────────
  if (/(^|\s)(approved|completed|delivered|sent|validated|exported|submitted|pass(ed)?)$/i.test(s))
    return 'ok';
  if (/result completed|sdms validated|sample(d)?$/i.test(s))
    return 'ok';
  // QA/QC Training Records: a qualified analyst is a done/green state.
  if (/^qualified$/i.test(s))
    return 'ok';

  // ── Failure / blocked — red ──────────────────────────────────────────
  // MOR Adhoc QC Status emits the bare word "Fail" — match it alongside
  // the existing failure synonyms.
  if (/(^|\s)fail(ed)?$/i.test(s))
    return 'alert';
  if (/(failed|cancel(l)?ed|rejected|rolled back|out of spec|do not deliver)/i.test(s))
    return 'alert';

  // ── Pending / waiting — amber for "needs your attention" actions ─────
  // (Approval / signing / waiting on people is amber; that's where the
  //  reviewer sees "I need to do something".)
  if (/(pending approval|pending signing|pending submit|pending result entry|waiting|in progress|in queue)/i.test(s))
    return 'warn';
  if (/(pending sampling|pending tier|pending for subout)/i.test(s))
    return 'warn';
  // QA/QC Training Records: "Pending QA Review" is waiting on a person, so it
  // belongs with the amber "needs your attention" group — not the blue
  // waiting-on-a-process one. Matched before the generic pending rules below.
  if (/pending qa review/i.test(s))
    return 'warn';
  // SDMS Result Validation/Approval: "Pending SDMS Validation" is amber in
  // ALPACA's list (analyst attention needed), not blue. Matched before the
  // generic "pending validation" blue rule below. (Merged 2026-07-07.)
  if (/pending sdms/i.test(s))
    return 'warn';

  // ── Pending validation / delivery — blue for "waiting on a process" ──
  if (/(pending validation|pending delivery|pending coa)/i.test(s))
    return 'info';
  if (/(pending receipt|pending transfer)/i.test(s))
    return 'info';

  // Default: blue / neutral
  return 'info';
}
